import { useCallback, useEffect, useState } from "react";
import { getTenantId } from "@/services/apiClient";

/**
 * Per-tenant localStorage-backed overrides for templated emails sent
 * via /api/v1/emails/send.
 *
 * Today this is the source of truth used by:
 *   - Settings → Email Templates page (read + write)
 *   - EnrollDeviceDialog email-invite flow (read INVITE_USER override)
 *
 * Once the backend exposes GET / PUT /api/v1/emails/templates we keep the
 * same hook surface but the EmailTemplatesSettings page also mirrors the
 * server response into localStorage, so trigger sites (dialogs, pages)
 * can keep reading synchronously without a network round-trip.
 *
 * Storage key shape: `emailTemplateOverrides:<tenantId | "default">`.
 * Cross-tab updates propagate via the native `storage` event plus a
 * custom `email-template-overrides:changed` window event for same-tab
 * subscribers (e.g. the dialog reading after the settings page saves).
 */

export interface EmailTemplateOverride {
  custom_subject?: string;
  custom_body?: string;
}

export type EmailTemplateOverridesMap = Record<string, EmailTemplateOverride>;

const STORAGE_PREFIX = "emailTemplateOverrides";
const SAME_TAB_EVENT = "email-template-overrides:changed";

function storageKey(): string {
  // Falls back to "default" so the page works pre-login or in single-tenant
  // mode where the JWT doesn't carry a tenantId claim.
  return `${STORAGE_PREFIX}:${getTenantId() ?? "default"}`;
}

function readFromStorage(): EmailTemplateOverridesMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(storageKey());
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as EmailTemplateOverridesMap) : {};
  } catch {
    // Corrupt / unparseable JSON → start fresh rather than crashing the UI.
    return {};
  }
}

function writeToStorage(map: EmailTemplateOverridesMap): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(), JSON.stringify(map));
  } catch {
    // Quota or privacy-mode failures are non-fatal; in-memory state still
    // reflects the change for the current session.
  }
}

/**
 * Reactive view of all template overrides for the current tenant.
 * Use this in the Settings UI where you need the full map plus mutators.
 */
export function useEmailTemplateOverrides() {
  const [map, setMap] = useState<EmailTemplateOverridesMap>(() => readFromStorage());

  // Keep state in sync with: other tabs (storage event) and other components
  // in the same tab (custom event dispatched by setOverride/clearOverride).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey()) setMap(readFromStorage());
    };
    const onCustom = () => setMap(readFromStorage());
    window.addEventListener("storage", onStorage);
    window.addEventListener(SAME_TAB_EVENT, onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SAME_TAB_EVENT, onCustom);
    };
  }, []);

  const persist = useCallback((next: EmailTemplateOverridesMap) => {
    writeToStorage(next);
    setMap(next);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(SAME_TAB_EVENT));
    }
  }, []);

  const setOverride = useCallback(
    (templateType: string, value: EmailTemplateOverride) => {
      // Strip empty fields so an "all-default" override doesn't linger as
      // an empty object that still flips the `isOverridden` indicator.
      const cleaned: EmailTemplateOverride = {};
      if (value.custom_subject && value.custom_subject.length > 0) {
        cleaned.custom_subject = value.custom_subject;
      }
      if (value.custom_body && value.custom_body.length > 0) {
        cleaned.custom_body = value.custom_body;
      }
      const next = { ...readFromStorage() };
      if (Object.keys(cleaned).length === 0) {
        delete next[templateType];
      } else {
        next[templateType] = cleaned;
      }
      persist(next);
    },
    [persist]
  );

  const clearOverride = useCallback(
    (templateType: string) => {
      const next = { ...readFromStorage() };
      delete next[templateType];
      persist(next);
    },
    [persist]
  );

  const replaceAll = useCallback(
    (next: EmailTemplateOverridesMap) => {
      persist(next);
    },
    [persist]
  );

  return { overrides: map, setOverride, clearOverride, replaceAll };
}

/**
 * Convenience read for a single template type. Returns `undefined` when
 * no override is configured. Trigger sites (e.g. EnrollDeviceDialog)
 * use this to spread `custom_subject` / `custom_body` into the
 * /api/v1/emails/send payload only when the admin actually customised it.
 */
export function useEmailTemplateOverride(templateType: string): EmailTemplateOverride | undefined {
  const { overrides } = useEmailTemplateOverrides();
  return overrides[templateType];
}

// Non-hook accessor for non-React contexts (e.g. one-off helpers).
export function getEmailTemplateOverride(templateType: string): EmailTemplateOverride | undefined {
  return readFromStorage()[templateType];
}
